import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle, useMemo, useCallback, memo } from 'react';
import 'ol/ol.css';
import { Map, View, Overlay } from 'ol';
import { defaults as defaultControls, Zoom, ScaleLine, OverviewMap } from 'ol/control';
import { defaults as defaultInteractions } from 'ol/interaction';
import TileLayer from 'ol/layer/Tile';
import VectorTileLayer from 'ol/layer/VectorTile';
import * as olSource from 'ol/source';
import { PMTilesVectorSource } from 'ol-pmtiles';
import { applyStyle } from 'ol-mapbox-style';
import { layers as pmLayers, namedFlavor } from '@protomaps/basemaps';
import { MouseWheelZoom, DragPan, DoubleClickZoom, PinchZoom, PinchRotate } from 'ol/interaction';
import PropTypes from 'prop-types';
import { fromLonLat, transform, get as getProjection } from 'ol/proj';
import styles from './Map.module.css';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import CircularProgress from '@mui/material/CircularProgress';

import { easeIn, easeOut } from 'ol/easing';

/**
 * OpenLayersMap Component - Extent-Based Map Implementation
 * 
 * This component uses ONLY extent-based fitting for map navigation:
 * - No hardcoded zoom levels (zoom values are ignored for navigation)
 * - All flyTo operations use extent/buffer-based calculations
 * - Initial view is fitted to view's ADM1 extent
 * - Zoom constraints (minZoom/maxZoom) only prevent user over/under-zooming
 * 
 * Navigation Methods:
 * - flyTo(center, duration, label) - uses 50km buffer around center point
 * - flyToExtent(extent, duration, label) - fits to provided extent with padding
 * - flyToDefault(duration) - fits to view's ADM1 extent
 */

const REFERENCE_WIDTH = 1200;
const REFERENCE_HEIGHT = 800;

// Configurable padding for flyToExtent (as percentage of extent dimensions)
// Adjust these values to control how much extra space is shown around the target extent
// Examples: 0.1 = 10% (tight), 0.25 = 25% (moderate), 0.4 = 40% (loose), 0.6 = 60% (very loose)
const EXTENT_PADDING = {
  left: 0.21,   // 21% padding on left (16% base + 5% extra)
  right: 0.16,  // 16% padding on right (original)
  top: 0.2,    // 21% padding on top (16% base + 5% extra)
  bottom: 0.094  // 16% padding on bottom (original)
};

const OpenLayersMap = forwardRef(({
  children,
  zoom = 2,  // Minimal default zoom, will be overridden by extent fitting
  minZoom = 1,  // Reasonable lower bound
  maxZoom = 20,  // Reasonable upper bound
  center,
  map,
  setMap,
  mapSettings,
  mapSettingsLoading,
  showScale = true,
  showOverview = true,
  overviewMapPosition = 'topRight',
  scaleUnits = 'metric',
  scaleBar = false,
  scaleBarPosition = 'bottomRight',
  enableMouseWheelZoom = false,
  enableDragPan = true,
  enableDoubleClickZoom,
  enableZoomControls = true,
  zoomControlsPosition = 'topRight',
  paddingBottom = 10,
  zoomDelta = 1,
  adm1Data,
  view,
}, ref) => {
  // One-time mount log with detailed tracking
  useEffect(() => {
    const mountInfo = {
      adm1Data: adm1Data?.length, 
      view: view?.iso3,
      hasMapSettings: !!mapSettings,
      timestamp: Date.now(),
      zoom: zoom,
      propsHash: JSON.stringify({
        hasAdm1Data: !!adm1Data?.length,
        hasView: !!view?.iso3,
        hasMapSettings: !!mapSettings,
        zoom: zoom
      })
    };
    // Component mount logging removed
    
    // Clean up on unmount
    return () => {
      // Component unmount logging removed
    };
  }, []);

  const mapElement = useRef();
  const labelRef = useRef();
  const [containerSize, setContainerSize] = useState({ width: REFERENCE_WIDTH, height: REFERENCE_HEIGHT });
  const [mapInstance, setMapInstance] = useState(null);
  const [labelOverlay, setLabelOverlay] = useState(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [isAdmin1ExtentFitted, setIsAdmin1ExtentFitted] = useState(false);
  
  // Calculate loading states for UI
  // We wait for:
  // 1. Essential map settings
  // 2. Initial extent fitting (if we have a view context) - this prevents the "zoom from world" flicker
  const isWaitingForEssentials = !mapSettings;
  const isWaitingForExtent = view?.iso3 && !isAdmin1ExtentFitted;
  
  const showLoadingIndicator = isWaitingForEssentials || isWaitingForExtent;
  
  // Hold overlay slightly longer after map is ready to mask final style adjustments
  const [showOverlay, setShowOverlay] = useState(true);

  // Control overlay visibility - only based on essential map readiness
  useEffect(() => {
    if (showLoadingIndicator || !isMapReady) {
      // Still loading essentials – ensure overlay is visible
      setShowOverlay(true);
    } else {
      // Map ready – hide overlay after short delay to avoid flash
      const timer = setTimeout(() => setShowOverlay(false), 300);
      return () => clearTimeout(timer);
    }
  }, [showLoadingIndicator, isMapReady]);
  
  // Reset admin1 extent fitted state when admin1 data changes (separate from map readiness)
  useEffect(() => {
    setIsAdmin1ExtentFitted(false);
  }, [view?.iso3, adm1Data?.length]);
  
  // Admin1 extent fitting is optional - if no admin1 data, that's fine
  useEffect(() => {
    if (!adm1Data || adm1Data.length === 0) {
      // No admin1 data available - that's perfectly fine, just mark as "fitted"
      setIsAdmin1ExtentFitted(true);
    }
  }, [adm1Data]);
  
  // Fallback timeout for admin1 extent fitting (doesn't affect map visibility)
  useEffect(() => {
    if (adm1Data && adm1Data.length > 0 && !isAdmin1ExtentFitted) {
      const fallbackTimer = setTimeout(() => {
        setIsAdmin1ExtentFitted(true);
      }, 3000); // 3 second fallback for admin1 extent fitting
      
      return () => clearTimeout(fallbackTimer);
    }
  }, [adm1Data, isAdmin1ExtentFitted]);
  const [overviewMap, setOverviewMap] = useState(null);
  
  // Add a flag to prevent multiple initializations
  const isInitializingRef = useRef(false);
  const isInitializedRef = useRef(false);

  // Observe container size
  useEffect(() => {
    if (!mapElement.current) return;
    const observer = new window.ResizeObserver(entries => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
      }
    });
    observer.observe(mapElement.current);
    return () => observer.disconnect();
  }, []);

  // Calculate extent from adm1Data - memoized to prevent unnecessary recalculations
  const calculateAdm1Extent = useMemo(() => {
    // Process data when we have actual data to calculate from
    if (!adm1Data || !Array.isArray(adm1Data) || adm1Data.length === 0) {
      return null;
    }
    
    const validExtents = adm1Data.map((region) => {
      if (region.zoom_extent_min_lon && region.zoom_extent_min_lat && region.zoom_extent_max_lon && region.zoom_extent_max_lat) {
        return [region.zoom_extent_min_lon, region.zoom_extent_min_lat, region.zoom_extent_max_lon, region.zoom_extent_max_lat];
      }
      
      if (region.zoom_extent && Array.isArray(region.zoom_extent) && region.zoom_extent.length === 4) {
        return region.zoom_extent;
      }
      
      return null;
    }).filter(extent => extent !== null);
    
    if (validExtents.length === 0) {
      return null;
    }
    
    const minLon = Math.min(...validExtents.map(extent => extent[0]));
    const minLat = Math.min(...validExtents.map(extent => extent[1]));
    const maxLon = Math.max(...validExtents.map(extent => extent[2]));
    const maxLat = Math.max(...validExtents.map(extent => extent[3]));
    
    const combinedExtent = [minLon, minLat, maxLon, maxLat];
    
    return combinedExtent;
  }, [adm1Data]);

  // Track when calculateAdm1Extent changes
  useEffect(() => {
    if (calculateAdm1Extent) {
      initialExtentFitted.current = false;
    }
  }, [calculateAdm1Extent]);

  // Get the refs from children
  const position = useMemo(() => {
    const refs = {};
    React.Children.forEach(children, child => {
      if (child?.props?.id === 'bottomRight') refs.bottomRight = child.ref;
      if (child?.props?.id === 'topRight') refs.topRight = child.ref;
      if (child?.props?.id === 'bottomLeft') refs.bottomLeft = child.ref;
      if (child?.props?.id === 'topLeft') refs.topLeft = child.ref;
    });
    return refs;
  }, [children]);

  // Simplified defaultView - only store extent, no zoom values
  const defaultView = useRef({
    center: fromLonLat([0, 0]),
    extent: null
  });
  const initialExtentFitted = useRef(false);

  // Add comprehensive debugging for view changes
  const logViewState = useCallback((context, view) => {
    console.log(`[OpenLayersMap] ${context} | Zoom: ${view?.getZoom()?.toFixed(2)} | Center: ${view?.getCenter()?.map(c => c.toFixed(2)).join(', ')}`);
  }, []);

  // Reset initial extent fitted flag when view changes
  const prevViewRef = useRef(view?.iso3);
  useEffect(() => {
    if (prevViewRef.current !== view?.iso3) {
      initialExtentFitted.current = false;
      prevViewRef.current = view?.iso3;
    }
  }, [view?.iso3]);

    // Main map initialization effect - only initialize once when requirements are met
  useEffect(() => {
    // Prevent multiple initializations
    if (isInitializingRef.current || isInitializedRef.current) {
      return;
    }

    // CRITICAL: Only wait for the map element
    if (!mapElement.current) {
      return;
    }

    isInitializingRef.current = true;

    const controls = [
      new Zoom({
        className: 'ol-zoom',
        delta: 1,
        zoomInLabel: '+',
        zoomOutLabel: '−'
      })
    ];

    if (showScale) {
      let scaleClassName = 'ol-scale-line';
      if (scaleBar) scaleClassName = 'ol-scale-bar';

      const targetRef = position[scaleBarPosition];
      const targetElement = targetRef && targetRef.current ? 
        targetRef.current.getElementsByClassName('scale')[0] : 
        null;

      const control = new ScaleLine({
        units: scaleUnits,
        bar: scaleBar,
        className: scaleClassName,
        minWidth: 200,
        maxWidth: 220,
        target: targetElement || undefined
      });
      controls.push(control);
    }

    if (showOverview) {
      // Use same PMTiles basemap as main map with matching light theme
      const overviewPmtilesUrl = `${window.location.origin}/tiles/basemap.pmtiles`;
      const layer = new VectorTileLayer({
        declutter: true,
        source: new PMTilesVectorSource({
          url: overviewPmtilesUrl,
          attributions: ['© OpenStreetMap'],
        }),
      });

      // Full custom flavor matching ProtomapsLayer's customLightFlavor
      const flavor = {
        ...namedFlavor('light'),
        background: '#c5d8e2',
        water: '#c5d8e2',
        earth: 'rgba(250, 250, 250, 0.85)',
        landcover: {
          grassland: 'rgba(240, 245, 238, 0.3)',
          barren: 'rgba(245, 243, 240, 0.2)',
          urban_area: 'rgba(245, 245, 245, 0.2)',
          farmland: 'rgba(242, 247, 240, 0.2)',
          glacier: 'rgba(252, 252, 255, 0.3)',
          scrub: 'rgba(242, 247, 240, 0.2)',
          forest: 'rgba(238, 245, 238, 0.3)',
        },
        park_a: 'rgba(242, 247, 243, 0.5)',
        park_b: 'rgba(234, 242, 235, 0.5)',
        hospital: 'rgba(250, 248, 248, 0.5)',
        industrial: 'rgba(246, 248, 249, 0.5)',
        school: 'rgba(250, 248, 246, 0.5)',
        wood_a: 'rgba(240, 245, 240, 0.4)',
        wood_b: 'rgba(232, 240, 232, 0.4)',
        pedestrian: 'rgba(248, 247, 245, 0.5)',
        scrub_a: 'rgba(242, 246, 244, 0.4)',
        scrub_b: 'rgba(232, 240, 236, 0.4)',
        glacier: 'rgba(250, 250, 250, 0.4)',
        sand: 'rgba(248, 246, 244, 0.4)',
        beach: 'rgba(250, 248, 244, 0.4)',
        aerodrome: 'rgba(244, 244, 246, 0.5)',
        runway: 'rgba(246, 246, 248, 0.5)',
        zoo: 'rgba(242, 246, 246, 0.5)',
        military: 'rgba(244, 244, 244, 0.5)',
        buildings: '#ececec',
        boundaries: '#015f94',
      };
      const overviewStyleSpec = {
        version: 8,
        glyphs: 'https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf',
        sprite: 'https://protomaps.github.io/basemaps-assets/sprites/v4/light',
        sources: { protomaps: { type: 'vector', url: overviewPmtilesUrl } },
        layers: (() => {
          const allLayers = pmLayers('protomaps', flavor, { lang: 'en' }).filter(l => {
            if (l.type === 'symbol') return false;
            if (l.id === 'boundaries_country' || l.id === 'boundaries') return false;
            return true;
          });
          // Thin admin-0 borders for the small overview map
          allLayers.push({
            id: 'admin-0-boundary',
            type: 'line',
            source: 'protomaps',
            'source-layer': 'boundaries',
            filter: ['<=', 'kind_detail', 2],
            paint: {
              'line-color': '#015f94',
              'line-width': 0.5,
            },
          });
          return allLayers;
        })(),
      };
      applyStyle(layer, overviewStyleSpec, { source: 'protomaps', updateSource: false })
        .then(() => console.log('[OverviewMap] PMTiles style applied'))
        .catch((err) => console.error('[OverviewMap] Style error:', err));

      const targetRef = position[overviewMapPosition];
      const targetElement = targetRef && targetRef.current ? 
        targetRef.current.getElementsByClassName('overview')[0] : 
        null;

      if (targetElement) {
        targetElement.innerHTML = '';
      }

      const overviewMapControl = new OverviewMap({
        className: 'ol-overviewmap ol-custom-overviewmap',
        layers: [layer],
        target: targetElement || undefined,
        collapsible: false,
        collapsed: false,
        minRatio: 0.35,
        maxRatio: 0.9,
      });
      controls.push(overviewMapControl);
      setOverviewMap(overviewMapControl);
    }

    const initialCenter = center || fromLonLat([0, 0]);

    const initialMap = new Map({
      target: mapElement.current,
      controls: controls,
      interactions: defaultInteractions({
        mouseWheelZoom: false,
        altShiftDragRotate: false,
        pinchRotate: false
      }),
      view: new View({
        center: initialCenter,
        zoom: zoom,
        minZoom: minZoom,
        maxZoom: maxZoom,
        constrainResolution: false,  // Allow fractional zoom levels for precise extent fitting
        projection: getProjection('EPSG:3857')
      }),
      layers: [],
      overlays: [],
    });

    setMapInstance(initialMap);
    isInitializedRef.current = true;
    isInitializingRef.current = false;
    
    // Map is now ready for display - set this immediately
    setIsMapReady(true);
    
    logViewState('After map initialization', initialMap.getView());
    
    if (setMap) setMap(initialMap);

    return () => {
      console.log('[OpenLayersMap] Cleanup triggered. Unmounting map instance.');
      isInitializedRef.current = false;
      isInitializingRef.current = false;
      
      if (overviewMap) {
        initialMap.removeControl(overviewMap);
      }
      initialMap.getControls().forEach((control) => {
        if (control instanceof ScaleLine) {
          initialMap.removeControl(control);
        }
      });
      initialMap.setTarget(undefined);
      initialMap.dispose();
    };
  }, []); // Empty dependency array - only run once on mount

  // Separate effect for overlay initialization
  useEffect(() => {
    if (!mapInstance) return;

    const overlay = new Overlay({
      element: labelRef.current,
      positioning: 'center-center',
      stopEvent: false
    });

    mapInstance.addOverlay(overlay);
    setLabelOverlay(overlay);

    return () => {
      if (mapInstance && overlay) {
        mapInstance.removeOverlay(overlay);
      }
    };
  }, [mapInstance]);

  // Apply zoom constraints to view
  useEffect(() => {
    if (!mapInstance || minZoom === undefined || maxZoom === undefined) return;

    const view = mapInstance.getView();
    if (!view) return;

    logViewState('Before setting zoom constraints', view);
    view.setMinZoom(minZoom);
    view.setMaxZoom(maxZoom);
    logViewState('After setting zoom constraints', view);
  }, [mapInstance, minZoom, maxZoom]);

  // Helper function to apply consistent padding to all extent fitting operations
  const fitExtentWithPadding = useCallback((view, extent, options = {}) => {
    if (!extent || extent.length !== 4) {
      return;
    }

    logViewState('Before fitExtentWithPadding', view);
    
    // Store current zoom constraints
    const currentMinZoom = view.getMinZoom();
    const currentMaxZoom = view.getMaxZoom();

    // If extent is already in mercator, use it directly; otherwise transform from WGS84
    let mercatorExtent;
    if (options.fromWGS84) {
      const [minLon, minLat, maxLon, maxLat] = extent;
      const bottomLeft = fromLonLat([minLon, minLat]);
      const topRight = fromLonLat([maxLon, maxLat]);
      mercatorExtent = [bottomLeft[0], bottomLeft[1], topRight[0], topRight[1]];
    } else {
      mercatorExtent = extent;
    }

    // Apply asymmetric padding
    const extentWidth = mercatorExtent[2] - mercatorExtent[0];
    const extentHeight = mercatorExtent[3] - mercatorExtent[1];
    const leftPadding = extentWidth * EXTENT_PADDING.left;
    const rightPadding = extentWidth * EXTENT_PADDING.right;
    const topPadding = extentHeight * EXTENT_PADDING.top;
    const bottomPadding = extentHeight * EXTENT_PADDING.bottom;

    const bufferedExtent = [
      mercatorExtent[0] - leftPadding,
      mercatorExtent[1] - bottomPadding,
      mercatorExtent[2] + rightPadding,
      mercatorExtent[3] + topPadding
    ];

    // Calculate what the natural zoom would be without constraints
    const mapSize = mapInstance.getSize();
    const naturalResolution = view.getResolutionForExtent(bufferedExtent, mapSize);
    const naturalZoom = view.getZoomForResolution(naturalResolution);

    // Apply the fit with the buffered extent
    const fitOptions = {
      size: mapInstance.getSize(),
      ...options,
      // Force OpenLayers to respect our calculated natural zoom (override any options)
      constrainResolution: false,  // Don't constrain to discrete zoom levels
      nearest: false,              // Don't round to nearest zoom level
      maxZoom: undefined,          // Remove any maxZoom constraints during fit
      minZoom: undefined           // Remove any minZoom constraints during fit
    };
    
    // Bypass view.fit() and manually set exact zoom and center for precise control
    const baseExtentCenter = [(bufferedExtent[0] + bufferedExtent[2]) / 2, (bufferedExtent[1] + bufferedExtent[3]) / 2];
    // Reuse mapSize from earlier calculation (line 466)
    const manualResolution = view.getResolutionForExtent(bufferedExtent, mapSize);
    const exactZoom = view.getZoomForResolution(manualResolution);
    
    // Apply y-offset of -20 pixels (move map up)
    const yOffsetPixels = -20;
    const yOffsetMapUnits = yOffsetPixels * manualResolution;
    const extentCenter = [baseExtentCenter[0], baseExtentCenter[1] + yOffsetMapUnits];
    
    // Set center and zoom with optional animation for exact precision
    if (options.duration) {
      // Use animation for smoother positioning
      view.animate({
        center: extentCenter,
        zoom: exactZoom,
        duration: options.duration,
        easing: easeOut
      });
    } else {
      // Set immediately for instant positioning
      view.setCenter(extentCenter);
      view.setZoom(exactZoom);
    }
    
    // Log the result after a brief delay to ensure the animation completes
    setTimeout(() => {
      logViewState('After fitExtentWithPadding (delayed)', view);
    }, 100);

  }, [mapInstance]);

  // Single effect to handle initial extent fitting
  useEffect(() => {
    if (!mapInstance || !calculateAdm1Extent || initialExtentFitted.current) {
      return;
    }
    
    const view = mapInstance.getView();
    
    // Ensure map size is updated before fitting
    mapInstance.updateSize();
    
    logViewState('Before initial extent fit', view);
    
        // Use a short delay to ensure map is fully rendered before applying extent
    setTimeout(() => {
      // Double-check map size before fitting
      const mapSize = mapInstance.getSize();
      
      if (!mapSize || mapSize[0] === 0 || mapSize[1] === 0) {
        mapInstance.updateSize();
      }
      
      // Use the helper function for consistent padding - let extent determine optimal zoom
      fitExtentWithPadding(view, calculateAdm1Extent, { 
        fromWGS84: true,
        context: 'INITIAL_LOAD'
        // No animation for initial load - use immediate positioning for stability
      });
      
      initialExtentFitted.current = true;
      defaultView.current.extent = calculateAdm1Extent;
      
      setIsAdmin1ExtentFitted(true); // ✅ Mark extent fitting as complete - map can now be visible
    }, 150); // Slightly longer delay to ensure map container is sized
    
    // Log final state after animation and track any subsequent changes
    const timeoutId = setTimeout(() => {
      try {
        
        if (view && mapInstance && mapInstance.getView() === view) {
          logViewState('Final state after initial extent fit', view);
          
          // Add a listener to detect if anything else moves the view after initial fitting
          const moveEndListener = () => {
            logViewState('View changed after initial extent fit', view);
            view.un('moveend', moveEndListener); // Remove after first detection
          };
          view.on('moveend', moveEndListener);
        }
        
      } catch (error) {
        // Silence error logging for performance
      }
    }, 150); // Timeout for immediate positioning check
    
  }, [mapInstance, calculateAdm1Extent, fitExtentWithPadding]);

  // Define callbacks outside of useImperativeHandle to avoid hook call issues
  const flyTo = useCallback((center, duration = 700, locationName = '') => {
    if (!mapInstance || !labelOverlay) {
      return;
    }
    
    const view = mapInstance.getView();
    logViewState('Before flyTo', view);
    
    const [lon, lat] = center;
    
    const normalizedLon = ((lon + 180) % 360) - 180;
    const normalizedLat = Math.max(-85, Math.min(85, lat));
    
    const baseTargetCenter = transform(
      [normalizedLon, normalizedLat],
      'EPSG:4326',
      'EPSG:3857'
    );
    
    // Apply y-offset of -20 pixels (move map up) for consistency with extent fitting
    const currentResolution = view.getResolution();
    const yOffsetPixels = -20;
    const yOffsetMapUnits = yOffsetPixels * currentResolution;
    const targetCenter = [baseTargetCenter[0], baseTargetCenter[1] + yOffsetMapUnits];
    
    if (locationName) {
      labelRef.current.innerHTML = `<div class="${styles.mapLabel}">${locationName}</div>`;
      labelOverlay.setPosition(targetCenter);
    } else {
      labelRef.current.innerHTML = '';
      labelOverlay.setPosition(undefined);
    }

    // Use extent-based approach instead of zoom level
    // Create a small extent around the center point for consistent zoom level
    const buffer = 50000; // 50km buffer in meters (adjust as needed)
    const extent = [
      targetCenter[0] - buffer,
      targetCenter[1] - buffer,
      targetCenter[0] + buffer,
      targetCenter[1] + buffer
    ];

    view.fit(extent, {
      duration: duration,
      easing: easeOut,
      size: mapInstance.getSize()
    });

    // Log final state after animation
    setTimeout(() => {
      logViewState('After flyTo animation completed', view);
    }, duration + 50);
  }, [mapInstance, labelOverlay]);

  const flyToExtent = useCallback((extent, duration = 700, locationName = '') => {
    if (!mapInstance || !extent || extent.length !== 4) {
      return;
    }

    const view = mapInstance.getView();
    logViewState('Before flyToExtent', view);
    
    view.cancelAnimations();

    if (labelOverlay) {
      labelOverlay.getElement().style.display = 'none';
    }

    const transformedExtent = extent.map((coord, index) => {
      if (index % 2 === 0) {
        return ((coord + 180) % 360) - 180;
      } else {
        return Math.max(-85, Math.min(85, coord));
      }
    });

    const mercatorExtent = transform(
      [transformedExtent[0], transformedExtent[1]],
      'EPSG:4326',
      'EPSG:3857'
    ).concat(
      transform(
        [transformedExtent[2], transformedExtent[3]],
        'EPSG:4326',
        'EPSG:3857'
      )
    );

    const extentWidth = mercatorExtent[2] - mercatorExtent[0];
    const extentHeight = mercatorExtent[3] - mercatorExtent[1];
    const leftPadding = extentWidth * EXTENT_PADDING.left;
    const rightPadding = extentWidth * EXTENT_PADDING.right;
    const topPadding = extentHeight * EXTENT_PADDING.top;
    const bottomPadding = extentHeight * EXTENT_PADDING.bottom;

    const bufferedExtent = [
      mercatorExtent[0] - leftPadding,
      mercatorExtent[1] - bottomPadding,
      mercatorExtent[2] + rightPadding,
      mercatorExtent[3] + topPadding
    ];

    const targetResolution = view.getResolutionForExtent(bufferedExtent);
    const baseCenterExtent = [
      (bufferedExtent[0] + bufferedExtent[2]) / 2,
      (bufferedExtent[1] + bufferedExtent[3]) / 2
    ];
    
    // Apply y-offset of -20 pixels (move map up)
    const yOffsetPixels = -20;
    const yOffsetMapUnits = yOffsetPixels * targetResolution;
    const targetCenter = [baseCenterExtent[0], baseCenterExtent[1] + yOffsetMapUnits];

    if (labelOverlay && locationName) {
      labelRef.current.innerHTML = `<div class="${styles.mapLabel}">${locationName}</div>`;
      labelOverlay.getElement().style.display = 'block';
      labelOverlay.setPosition(targetCenter);
    }

    view.animate({
      center: targetCenter,
      resolution: targetResolution,
      duration: duration,
      easing: easeOut,
    });

    // Log final state after animation
    setTimeout(() => {
      logViewState('After flyToExtent animation completed', view);
    }, duration + 50);
  }, [mapInstance, labelOverlay]);

  const flyToDefault = useCallback((duration = 700) => {
    if (!mapInstance) {
      return;
    }

    const view = mapInstance.getView();
    logViewState('Before flyToDefault', view);
    
    view.cancelAnimations();

    if (labelOverlay) {
      labelOverlay.getElement().style.display = 'none';
    }

    let targetExtent;
    if (calculateAdm1Extent) {
      targetExtent = calculateAdm1Extent;
      // Use helper function for consistent padding - let extent determine optimal zoom
      fitExtentWithPadding(view, targetExtent, { 
        fromWGS84: true,
        context: 'RESET_FLYTODEFAULT',
        duration: duration  // Add animation duration
      });
    } else if (defaultView.current.extent) {
      targetExtent = defaultView.current.extent;
      // Use helper function for consistent padding - let extent determine optimal zoom
      fitExtentWithPadding(view, targetExtent, { 
        fromWGS84: true,
        context: 'RESET_FLYTODEFAULT',
        duration: duration  // Add animation duration
      });
    } else {
      const worldExtent = [
        fromLonLat([-180, -90]),
        fromLonLat([180, 90])
      ];
      targetExtent = [worldExtent[0][0], worldExtent[0][1], worldExtent[1][0], worldExtent[1][1]];
      view.fit(targetExtent, {
        duration: duration,
        easing: easeOut,
        maxZoom: 7  // Keep maxZoom for world extent to prevent over-zooming
      });
    }

    setTimeout(() => {
      logViewState('After flyToDefault animation completed', view);
    }, duration + 100);
  }, [mapInstance, labelOverlay, calculateAdm1Extent, fitExtentWithPadding]);

  // Expose methods via useImperativeHandle
  useImperativeHandle(ref, () => ({
    flyTo,
    flyToExtent,
    flyToDefault
  }), [flyTo, flyToExtent, flyToDefault]);
  
  return (
    <div style={{ height: '100%', position: 'relative' }}>
      {/* Loading overlay */}
      {showOverlay && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255,255,255,1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
                          <CircularProgress size={48} thickness={4} sx={{ color: 'var(--color-primary)' }} />
        </div>
      )}

      {/* Actual map container */}
      <div
        ref={mapElement}
        className="ol-map"
        style={{
          height: '100%',
          width: '100%',
          position: 'relative',
          // Keep map hidden until basic map setup is ready to avoid flicker
          visibility: isMapReady ? 'visible' : 'hidden',
          transition: 'visibility 0s linear 0.2s', // small delay so overlay fades first
        }}
      >
        {children}
        <div ref={labelRef} className={styles.mapLabelContainer} />
      </div>
    </div>
  );
});

OpenLayersMap.displayName = 'OpenLayersMap';

OpenLayersMap.propTypes = {
  children: PropTypes.node,
  zoom: PropTypes.number,
  minZoom: PropTypes.number,
  maxZoom: PropTypes.number,
  center: PropTypes.array,
  map: PropTypes.object,
  setMap: PropTypes.func,
  mapSettings: PropTypes.object,
  mapSettingsLoading: PropTypes.bool,
  showScale: PropTypes.bool,
  showOverview: PropTypes.bool,
  overviewMapPosition: PropTypes.string,
  scaleUnits: PropTypes.string,
  scaleBar: PropTypes.bool,
  scaleBarPosition: PropTypes.string,
  enableMouseWheelZoom: PropTypes.bool,
  enableDragPan: PropTypes.bool,
  enableDoubleClickZoom: PropTypes.bool,
  enableZoomControls: PropTypes.bool,
  zoomControlsPosition: PropTypes.string,
  paddingBottom: PropTypes.number,
  zoomDelta: PropTypes.number,
};

export default OpenLayersMap;
